import { server } from "../../server";
import { useEffect, useState } from "react";
import axios from "axios";
import ExportExcel from "../../components/ExportExcel";
import ExportPDF from "../../components/ExportPDF";

function SchoolAttendance() {
  const [attendanceDetails, setAttendanceDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const recordsPerPage = 10;
  const todayDate = new Date().toISOString().split("T")[0];
  const [presentCount, setPresentCount] = useState(0);
  const [absentCount, setAbsentCount] = useState(0);

  useEffect(() => {
    const fetchAttendanceDetails = async () => {
      try {
        const response = await axios.post(
          `${server}/student_attendance_of_school_bydate_mongodb`,
          { date: todayDate },
          {
            headers: {
              Authorization: `${localStorage.getItem("token")}`,
            },
          }
        );

        const parsedBody = JSON.parse(response.data["body-json"]?.body || "{}");
        const attendanceData = parsedBody.schoolAttendance;

        if (attendanceData && Array.isArray(attendanceData)) {
          setAttendanceDetails(attendanceData);

          const presentCount = attendanceData.filter(
            (student) => student.school?.status === "Present"
          ).length;

          const absentCount = attendanceData.filter(
            (student) => student.school?.status === "Absent"
          ).length;

          setPresentCount(presentCount);
          setAbsentCount(absentCount);
        } else {
          console.error("Invalid data format from API", response.data);
        }
      } catch (error) {
        console.error("Error fetching attendance details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAttendanceDetails();
  }, []);

  // Filter
  const filteredRecords = attendanceDetails.filter((scholarship) => {
    const searchLower = searchTerm.toLowerCase();

    return Object.values(scholarship).some((value) => {
      if (typeof value === "string") {
        return value.toLowerCase().includes(searchLower);
      } else if (typeof value === "object" && value !== null) {
        return Object.values(value).some(
          (subValue) =>
            typeof subValue === "string" &&
            subValue.toLowerCase().includes(searchLower)
        );
      }
      return false;
    });
  });

  // ✅ Reset pagination when searching
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // ✅ Apply pagination AFTER filtering
  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredRecords.slice(
    indexOfFirstRecord,
    indexOfLastRecord
  );

  // Page Navigation
  const nextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const prevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const generatePageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 3;

    if (totalPages <= maxVisiblePages) {
      // Show all pages if less than maxVisiblePages
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // Always show first & last page
      pageNumbers.push(1);

      let startPage = Math.max(2, currentPage - 1);
      let endPage = Math.min(totalPages - 1, currentPage + 1);

      if (currentPage <= 2) {
        endPage = 3;
      } else if (currentPage >= totalPages - 1) {
        startPage = totalPages - 2;
      }

      if (startPage > 2) pageNumbers.push("...");
      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }
      if (endPage < totalPages - 1) pageNumbers.push("...");

      pageNumbers.push(totalPages);
    }
    return pageNumbers;
  };

  const columnMapping = {
    "studentDetails.admission_no": "Admission No",
    "studentDetails.name": "Name",
    "studentDetails.currentSection": "Section",
    "studentDetails.rollNo": "Roll No",
    "studentDetails.phoneNo": "Phone No",
    "studentDetails.gender": "Gender",
    "scholarship.type": "Type",
    "scholarship.siblingAdmissionNo": "Sibling Admission No",
    "scholarship.employeeId": "Employee Id",
    "scholarship.feeDiscount.admissionFee": "Admission Fee",
    "scholarship.feeDiscount.monthlyFee": "Monthly Fee",
    "scholarship.feeDiscount.developmentCharges": "Development Charges",
  };

  const handleExportExcel = () => {
    ExportExcel(attendanceDetails, "ScholarshipDetails", columnMapping);
  };

  const handleExportPDF = () => {
    ExportPDF(attendanceDetails, "ScholarshipDetails", columnMapping);
  };

  return (
    <>
      <div id="db-wrapper">
        <main id="page-content">
          <div className="">
            <div class="container-fluid my-3 px-3">
              <div className="row g-2">
                <div className="col-lg-8">
                  <div class="table-responsive border-0 overflow-y-hidden bg-white rounded-4">
                    <table class="table mb-0 text-nowrap table-centered">
                      <thead class="table-light">
                        <tr>
                          <th className="w-10">No.</th>
                          <th className="lh-1 w-60">
                            Details
                            <i className="bi bi-info-circle ms-2 text-dark"></i>
                            <br />
                            <span className="fs-8 fw-normal text-secondary">
                              Displaying Information as per Record
                            </span>
                          </th>
                          <th className="lh-1 w-30">
                            Action
                            <i className="bi bi-person-gear ms-2 text-dark"></i>
                            <br />
                            <span className="fs-8 fw-normal text-secondary">
                              Role-Specific Action Panel
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentRecords.map((attendance, index) => (
                          <tr>
                            <td>{indexOfFirstRecord + index + 1}.</td>
                            <td className="lh-sm py-4 text-wrap">
                              <span class="text-dark">Admission No</span>{" "}
                              &mdash; {attendance.admission_no} <br />
                              <span class="text-dark">Student Name</span>{" "}
                              &mdash; {attendance.name} <br />
                              <span class="text-dark">Class</span> &mdash;{" "}
                              {attendance.class} <br />
                              <span class="text-dark">
                                Section
                              </span> &mdash; {attendance.section} <br />
                              <span class="text-dark">
                                Roll No
                              </span> &mdash; {attendance.rollNo}
                              <hr className="w-25" />
                              <span class="text-dark">Status</span> &mdash;{" "}
                              {attendance?.school?.status}
                              <br />
                              <span class="text-dark">
                                Created By
                              </span> &mdash; {attendance?.school?.createdBy}
                            </td>
                            <td>
                              <div className="d-flex flex-column gap-1 width-fit-content">
                                <span class="badge bg-danger-soft cursor-pointer px-4 rounded-3 btn-py-sm">
                                  Update
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="col-lg-4">
                  <div class="">
                    <div className="mb-3">
                      <div className="bg-white rounded-pill">
                        <form
                          className="d-flex align-items-center"
                          onSubmit={(e) => e.preventDefault()}
                        >
                          <span className="position-absolute ps-3 search-icon">
                            <i className="bi bi-filter-circle-fill ms-1" />
                          </span>
                          <input
                            type="search"
                            className="form-control ps-7 rounded-3 border-0 rounded-4"
                            placeholder="Filter Data"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </form>
                      </div>
                      <div className="mt-2 ms-4">
                        <nav aria-label="breadcrumb">
                          <ol class="breadcrumb pe-3">
                            <li class="breadcrumb-item">
                              <a href="#">Attendance</a>
                            </li>
                            <li
                              class="breadcrumb-item active"
                              aria-current="page"
                            >
                              School Attendance
                            </li>
                          </ol>
                        </nav>
                      </div>
                    </div>

                    <div className="p-4 bg-white rounded-4">
                      <div class="bg-danger-soft rounded-4 p-3 d-flex align-items-center">
                        <div class="avatar-group">
                          {[1, 2, 3, 4, 5].map((num) => (
                            <span className="avatar avatar-md" key={num}>
                              <img
                                alt={`avatar-${num}`}
                                src={`/assets/images/students/${num}.jpg`}
                                className="rounded-circle"
                              />
                            </span>
                          ))}
                        </div>
                        <h3 className="fw-bold m-0 text-center fs-4 ms-3">
                          {attendanceDetails.length.toLocaleString("en-IN")}
                        </h3>
                      </div>

                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <div className="d-flex align-items-center">
                          {loading ? (
                            <div
                              className="spinner-border text-dark data-loader-spinner"
                              role="status"
                            ></div>
                          ) : (
                            <img src="/assets/images/folder.svg" />
                          )}

                          <p className="my-0 fw-semibold lh-1 fs-5 text-dark mx-3">
                            School Attendance
                            <br />
                            <span className="text-secondary fs-6 fw-medium lh-sm">
                              1.8 MB Size
                            </span>
                          </p>
                        </div>
                        <span className="badge bg-pink rounded-3 px-4 py-2 me-1 mt-1 cursor-pointer">
                          Export
                        </span>
                      </div>

                      <div class="px-4 py-2 mt-3 bg-light rounded-4">
                        <li className="list-group-item px-0">
                          <a
                            className="d-flex align-items-center text-inherit mb-0 justify-content-between"
                            data-bs-toggle="collapse"
                            href="#specifications"
                            role="button"
                            aria-expanded="false"
                            aria-controls="specifications"
                          >
                            <p className="m-0 fw-medium lh-sm text-dark">
                              Download Report
                            </p>
                            <span className="chevron-arrow ms-4">
                              <i className="bi bi-chevron-down fs-4" />
                            </span>
                          </a>
                          <div
                            className="collapse"
                            id="specifications"
                            data-bs-parent="#ecommerceAccordion"
                          >
                            <div className="py-3">
                              <div class="rounded-4 d-flex flex-wrap">
                                <span class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1">
                                  Class Wise
                                </span>
                                <span class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1">
                                  Attendance Type Wise
                                </span>
                                <span class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1">
                                  Bus Wise
                                </span>
                                <span class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1">
                                  All Students Attendance
                                </span>
                              </div>
                            </div>
                          </div>
                        </li>
                      </div>

                      <div class="mt-1">
                        <div className="row g-1">
                          <div className="col-12">
                            <div className="p-3 bg-danger-soft rounded-4">
                              <h4 className="m-0 text-center lh-1">
                                {presentCount}/{absentCount}
                              </h4>
                              <p className="text-center fs-5 m-0 lh-1 mt-1">
                                Students
                                <br />
                                (Present / Absent)
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="my-4">
                      <nav>
                        <ul className="pagination justify-content-center mb-0">
                          <li
                            className={`page-item ${
                              currentPage === 1 ? "disabled" : ""
                            }`}
                          >
                            <button className="page-link" onClick={prevPage}>
                              <i className="bi bi-chevron-left"></i>
                            </button>
                          </li>

                          {(() => {
                            const pageNumbers = [];
                            pageNumbers.push(1);

                            if (currentPage > 3) {
                              pageNumbers.push("left-ellipsis");
                            }

                            if (
                              currentPage !== 1 &&
                              currentPage !== totalPages
                            ) {
                              pageNumbers.push(currentPage);
                            }

                            if (currentPage < totalPages - 2) {
                              pageNumbers.push("right-ellipsis");
                            }

                            if (totalPages > 1) {
                              pageNumbers.push(totalPages);
                            }

                            return pageNumbers.map((page, index) => {
                              if (
                                page === "left-ellipsis" ||
                                page === "right-ellipsis"
                              ) {
                                return (
                                  <li
                                    key={index}
                                    className="page-item disabled"
                                  >
                                    <span className="page-link">...</span>
                                  </li>
                                );
                              }

                              return (
                                <li
                                  key={index}
                                  className={`page-item ${
                                    currentPage === page ? "active" : ""
                                  }`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              );
                            });
                          })()}

                          <li
                            className={`page-item ${
                              currentPage === totalPages ? "disabled" : ""
                            }`}
                          >
                            <button className="page-link" onClick={nextPage}>
                              <i className="bi bi-chevron-right"></i>
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export default SchoolAttendance;
